import * as React from 'react'
import { ownerDocument, clamp } from '@applift/factor'
import { GridEventListener, GridEventLookup } from '../../../models/events'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import {
  GridFocusApi,
  GridFocusPrivateApi,
} from '../../../models/api/gridFocusApi'
import { GridCellParams } from '../../../models/params/gridCellParams'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { isKeyboardEvent, isNavigationKey } from '../../../utils/keyboardUtils'
import { gridFocusCellSelector } from './gridFocusStateSelector'
import { GridStateInitializer } from '../../utils/useGridInitializeState'
import { GridCellCoordinates } from '../../../models/gridCell'

export const focusStateInitializer: GridStateInitializer = state => ({
  ...state,
  // @ts-ignore
  focus: { cell: null, columnHeader: null },
  // @ts-ignore
  tabIndex: { cell: null, columnHeader: null },
})

export const useGridFocus = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>
): void => {
  const lastClickedCell = React.useRef<GridCellParams | null>(null)

  const publishCellFocusOut = React.useCallback(
    (
      cell: GridCellCoordinates | null,
      event: GridEventLookup['cellFocusOut']['event']
    ) => {
      if (cell) {
        const isEditable = apiRef.current.isCellEditable(cell)
        const cellMode = apiRef.current.getCellMode(cell.rowId, cell.field)
        // The row might have been deleted
        if (apiRef.current.tableInstance.getRow(cell.rowId)) {
          apiRef.current.publishEvent(
            'cellFocusOut',
            { ...cell, isEditable, cellMode },
            event
          )
        }
      }
    },
    [apiRef]
  )

  const setCellFocus = React.useCallback<GridFocusApi['setCellFocus']>(
    (rowId, field) => {
      const focusedCell = gridFocusCellSelector(apiRef)
      if (focusedCell?.rowId === rowId && focusedCell?.field === field) {
        return
      }

      apiRef.current.setState(state => {
        return {
          ...state,
          tabIndex: {
            cell: { rowId, field },
            columnHeader: null,
          },
          focus: {
            cell: { rowId, field },
            columnHeader: null,
          },
        }
      })
      apiRef.current.forceUpdate()

      // The row might have been deleted
      if (!apiRef.current.tableInstance.getRow(rowId)) {
        return
      }

      if (focusedCell) {
        // There's a focused cell but another cell was clicked
        // Publishes an event to notify that the focus was lost
        publishCellFocusOut(focusedCell, {})
      }
      const isEditable = apiRef.current.isCellEditable({ rowId, field })
      const cellMode = apiRef.current.getCellMode(rowId, field)
      apiRef.current.publishEvent('cellFocusIn', {
        rowId,
        field,
        isEditable,
        cellMode,
      })
    },
    [apiRef, publishCellFocusOut]
  )

  const setColumnHeaderFocus = React.useCallback<
    GridFocusApi['setColumnHeaderFocus']
  >(
    (field, event = {}) => {
      const cell = gridFocusCellSelector(apiRef)
      publishCellFocusOut(cell, event)

      apiRef.current.setState(state => {
        return {
          ...state,
          tabIndex: {
            columnHeader: { field },
            cell: null,
          },
          focus: {
            columnHeader: { field },
            cell: null,
          },
        }
      })

      apiRef.current.forceUpdate()
    },
    [apiRef, publishCellFocusOut]
  )

  const moveFocusToRelativeCell = React.useCallback<
    GridFocusPrivateApi['moveFocusToRelativeCell']
  >(
    (rowId, field, direction) => {
      const leftColumns =
        apiRef.current.tableInstance.getLeftVisibleLeafColumns()

      const centerColumns =
        apiRef.current.tableInstance.getCenterVisibleLeafColumns()

      const rightColumns =
        apiRef.current.tableInstance.getRightVisibleLeafColumns()

      const visibleColumns = [
        ...leftColumns,
        ...centerColumns,
        ...rightColumns,
      ].map(one => one.id)
      let rowIndexToFocus = apiRef.current.tableInstance.getRow(rowId).index
      let columnIndexToFocus = visibleColumns.indexOf(field)
      if (direction === 'right') {
        columnIndexToFocus += 1
      } else if (direction === 'left') {
        columnIndexToFocus -= 1
      } else {
        rowIndexToFocus += 1
      }

      const rows = apiRef.current.tableInstance.getRowModel().flatRows

      if (columnIndexToFocus >= visibleColumns.length) {
        // Go to next row if we are after the last column
        rowIndexToFocus += 1

        if (rowIndexToFocus < rows.length) {
          // Go to first column of the next row if there's one more row
          columnIndexToFocus = 0
        }
      } else if (columnIndexToFocus < 0) {
        // Go to previous row if we are before the first column
        rowIndexToFocus -= 1

        if (rowIndexToFocus >= 0) {
          // Go to last column of the previous if there's one more row
          columnIndexToFocus = visibleColumns.length - 1
        }
      }

      rowIndexToFocus = clamp(rowIndexToFocus, 0, rows.length - 1)
      const rowToFocus = rows[rowIndexToFocus]

      if (!rowToFocus) {
        return
      }

      columnIndexToFocus = clamp(
        columnIndexToFocus,
        0,
        visibleColumns.length - 1
      )
      const columnToFocus = visibleColumns[columnIndexToFocus]
      if (rowToFocus && columnToFocus) {
        apiRef.current.setCellFocus(rowToFocus?.id, columnToFocus)
      }
    },
    [apiRef]
  )

  const handleCellDoubleClick = React.useCallback<
    GridEventListener<'cellDoubleClick'>
  >(
    ({ rowId, field }) => {
      apiRef.current.setCellFocus(rowId, field)
    },
    [apiRef]
  )

  const handleCellKeyDown = React.useCallback<GridEventListener<'cellKeyDown'>>(
    (params, event) => {
      // GRID_CELL_NAVIGATION_KEY_DOWN handles the focus on Enter, Tab and navigation keys
      if (
        event.key === 'Enter' ||
        event.key === 'Tab' ||
        event.key === 'Shift' ||
        isNavigationKey(event.key)
      ) {
        return
      }
      apiRef.current.setCellFocus(params.rowId, params.field)
    },
    [apiRef]
  )

  const handleColumnHeaderFocus = React.useCallback<
    GridEventListener<'columnHeaderFocus'>
  >(
    ({ field }, event) => {
      if (event.target !== event.currentTarget) {
        return
      }
      apiRef.current.setColumnHeaderFocus(field, event)
    },
    [apiRef]
  )

  const handleBlur = React.useCallback<
    GridEventListener<'columnHeaderBlur'>
  >(() => {
    apiRef.current.setState(state => ({
      ...state,
      focus: { cell: null, columnHeader: null },
    }))
  }, [apiRef])

  const handleCellMouseDown = React.useCallback<
    GridEventListener<'cellMouseDown'>
  >(params => {
    lastClickedCell.current = params
  }, [])

  const handleDocumentClick = React.useCallback(
    (event: MouseEvent) => {
      const cellParams = lastClickedCell.current
      lastClickedCell.current = null

      const focusedCell = gridFocusCellSelector(apiRef)

      const _canUpdateFocus = canUpdateFocus(true, {
        event,
        cell: cellParams,
      })

      if (!_canUpdateFocus) {
        return
      }

      if (!focusedCell) {
        if (cellParams) {
          apiRef.current.setCellFocus(cellParams.rowId, cellParams.field)
        }
        return
      }

      if (
        cellParams?.rowId === focusedCell.rowId &&
        cellParams?.field === focusedCell.field
      ) {
        return
      }

      const cellElement = apiRef.current.getCellElement(
        focusedCell.rowId,
        focusedCell.field
      )
      if (cellElement?.contains(event.target as HTMLElement)) {
        return
      }

      if (cellParams) {
        apiRef.current.setCellFocus(cellParams.rowId, cellParams.field)
      } else {
        apiRef.current.setState(state => ({
          ...state,
          focus: { cell: null, columnHeader: null },
        }))
        apiRef.current.forceUpdate()

        // There's a focused cell but another element (not a cell) was clicked
        // Publishes an event to notify that the focus was lost
        publishCellFocusOut(focusedCell, event)
      }
    },
    [apiRef, publishCellFocusOut]
  )

  const handleCellModeChange = React.useCallback<
    GridEventListener<'cellModeChange'>
  >(
    params => {
      if (params.cellMode === 'view') {
        return
      }
      const cell = gridFocusCellSelector(apiRef)
      if (cell?.rowId !== params.rowId || cell?.field !== params.field) {
        apiRef.current.setCellFocus(params.rowId, params.field)
      }
    },
    [apiRef]
  )

  const focusApi: GridFocusApi = {
    setCellFocus,
    setColumnHeaderFocus,
  }

  const focusPrivateApi: GridFocusPrivateApi = {
    moveFocusToRelativeCell,
  }

  useGridApiMethod(apiRef, focusApi, 'public')
  useGridApiMethod(apiRef, focusPrivateApi, 'private')

  React.useEffect(() => {
    const doc = ownerDocument(apiRef.current.rootElementRef!.current)
    doc.addEventListener('click', handleDocumentClick)

    return () => {
      doc.removeEventListener('click', handleDocumentClick)
    }
  }, [apiRef, handleDocumentClick])

  useGridApiEventHandler(apiRef, 'columnHeaderBlur', handleBlur)
  useGridApiEventHandler(apiRef, 'cellDoubleClick', handleCellDoubleClick)
  useGridApiEventHandler(apiRef, 'cellMouseDown', handleCellMouseDown)
  useGridApiEventHandler(apiRef, 'cellKeyDown', handleCellKeyDown)
  useGridApiEventHandler(apiRef, 'cellModeChange', handleCellModeChange)
  useGridApiEventHandler(apiRef, 'columnHeaderFocus', handleColumnHeaderFocus)
}

export const canUpdateFocus = function (
  initialValue: boolean,
  context: {
    event: MouseEvent | React.KeyboardEvent
    cell: GridCellParams | null
  }
) {
  if (!context.cell || !context.event.shiftKey) {
    return initialValue
  }
  if (isKeyboardEvent(context.event)) {
    return isNavigationKey(context.event.key) ? false : initialValue
  }
  return initialValue
}
